/**
 * Calcula a posição de scroll necessária para centralizar o "now" na tela
 * @returns scrollLeft em pixels ou null se não deve fazer scroll
 */
export function calculateScrollToNow(
  date: Date,
  startHour: number,
  endHour: number,
  fixedWidth: number,
  containerWidth: number,
  maxScrollLeft?: number
): number | null {
  const day = date.toISOString().split("T")[0];
  const start = new Date(`${day}T${String(startHour).padStart(2, "0")}:00:00`);
  
  // Se endHour < startHour, significa que é do dia seguinte
  let end: Date;
  if (endHour < startHour) {
    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 1);
    const nextDayStr = nextDay.toISOString().split("T")[0];
    end = new Date(`${nextDayStr}T${String(endHour).padStart(2, "0")}:00:00`);
  } else {
    end = new Date(`${day}T${String(endHour).padStart(2, "0")}:00:00`);
  }
  
  const now = new Date();

  // Se o now está fora do range, não faz scroll
  if (now < start || now > end) {
    return null;
  }

  const MINUTES_MS = 1000 * 60;
  const totalMinutes = (end.getTime() - start.getTime()) / MINUTES_MS;
  const nowMinutes = (now.getTime() - start.getTime()) / MINUTES_MS;

  const nowLeftPx = (nowMinutes / totalMinutes) * fixedWidth;

  // Calcula scroll para manter o "now" no centro
  const scrollLeft = nowLeftPx - (containerWidth / 2);
  
  // Se maxScrollLeft foi fornecido e já chegou ao fim, não faz scroll
  if (maxScrollLeft !== undefined && scrollLeft >= maxScrollLeft) {
    return null;
  }

  // Retorna a posição de scroll limitada entre 0 e maxScrollLeft (se fornecido)
  if (maxScrollLeft !== undefined) {
    return Math.max(0, Math.min(scrollLeft, maxScrollLeft));
  }
  
  return Math.max(0, scrollLeft);
}

/**
 * Atualiza a posição de scroll do container para centralizar o "now"
 * @param container - Elemento HTML do container
 * @param date - Data base para cálculo
 * @param startHour - Hora inicial
 * @param endHour - Hora final
 * @param fixedWidth - Largura fixa do conteúdo
 * @param maxScrollLeft - Scroll máximo opcional
 * @param isProgrammaticScrollRef - Ref opcional para marcar scroll programático
 * @returns true se o scroll foi atualizado, false caso contrário
 */
export function updateScrollToNow(
  container: HTMLDivElement,
  date: Date,
  startHour: number,
  endHour: number,
  fixedWidth: number,
  maxScrollLeft?: number,
  isProgrammaticScrollRef?: { current: boolean }
): boolean {
  const visibleWidth = container.clientWidth;
  const scrollLeft = calculateScrollToNow(
    date,
    startHour,
    endHour,
    fixedWidth,
    visibleWidth,
    maxScrollLeft
  );

  if (scrollLeft === null) {
    return false;
  }

  // Marca como scroll programático antes de fazer o scroll
  if (isProgrammaticScrollRef) {
    isProgrammaticScrollRef.current = true;
  }
  container.scrollLeft = scrollLeft;
  return true;
}
